import { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { cargarPdfPagado } from '../services/declaracionRenta/f22Service';
import { useAuth } from '../context/AuthContext';

/**
 * Subida del certificado de renta pagada (el F-22 presentado ante el SII).
 *
 * Único lugar con la lógica de "elegir PDF → extraer códigos con Claude →
 * guardar", porque se ofrece desde tres puntos distintos: el botón del
 * formulario, el botón de la renta ya calculada, y el "Reemplazar PDF" de la
 * página del certificado. El backend hace UPSERT y borra el PDF anterior de
 * R2, así que cargar y reemplazar son exactamente la misma operación.
 *
 * @param {number} anioTributario
 * @param {string} regimen
 * @param {(row: object) => void} onCargado - se llama con la fila guardada.
 * @returns {{ cargando: boolean, seleccionarArchivo: () => void, inputProps: object }}
 *   `inputProps` se esparce sobre un <input type="file"> oculto que el
 *   consumidor debe renderizar.
 */
const useCargarCertificadoPagado = (anioTributario, regimen, onCargado) => {
  const { selectedCompany } = useAuth();
  const idCompany = selectedCompany?.id_company || selectedCompany?.id;

  const [cargando, setCargando] = useState(false);
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      Swal.fire('Error', 'El archivo debe ser un PDF', 'error');
      return;
    }

    setCargando(true);
    Swal.fire({
      title: 'Procesando certificado...',
      html: 'Claude está extrayendo los códigos declarados...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const result = await cargarPdfPagado(idCompany, anioTributario, regimen, file);
      Swal.close();
      if (!result.success) {
        Swal.fire('Error', result.message || 'No se pudo procesar el PDF', 'error');
        return;
      }
      if (onCargado) await onCargado(result.data);
    } catch (error) {
      Swal.close();
      Swal.fire('Error', error.response?.data?.message || 'No se pudo procesar el PDF', 'error');
    } finally {
      setCargando(false);
    }
  };

  return {
    cargando,
    seleccionarArchivo: () => inputRef.current?.click(),
    inputProps: {
      type: 'file',
      ref: inputRef,
      accept: 'application/pdf',
      onChange: handleChange,
      style: { display: 'none' },
    },
  };
};

export default useCargarCertificadoPagado;
